import {
  EGender,
  ELanguage,
  EMaritalStatus,
  EProvince,
  getEGenderList,
  getELanguageList,
  getEProvinceList,
} from '@if/core/types'
import { CreditApplication } from '@src/data/types'
import { MonolithLoan } from '@src/data/types/MonolithLoan'

type FormApplicant = CreditApplication['applicant']

function keepText<T>(current: T, incoming: string | null | undefined): T | string {
  return incoming != null && incoming.trim() !== '' ? incoming.trim() : current
}

function keepDigits<T>(current: T, incoming: string | null | undefined): T | string {
  const digits = incoming?.replace(/\D/g, '') ?? ''
  return digits === '' ? current : digits
}

function keepEnum<TCurrent, TEnum extends number>(
  current: TCurrent,
  incoming: number | null | undefined,
  allowed: TEnum[],
): TCurrent | TEnum {
  return incoming != null && allowed.includes(incoming as TEnum) ? (incoming as TEnum) : current
}

function keepProvince<T>(current: T, incoming: string | null | undefined): T | EProvince {
  const province = incoming?.trim().toUpperCase() as EProvince
  return province && getEProvinceList().includes(province) ? province : current
}

export function buildApplicantSeedFromMonolithLoan(applicant: FormApplicant, loan: MonolithLoan): FormApplicant {
  const customer = loan.customer
  const address = customer.currentAddress

  return {
    ...applicant,
    firstName: keepText(applicant.firstName, customer.firstName),
    middleName: keepText(applicant.middleName, customer.middleName),
    lastName: keepText(applicant.lastName, customer.lastName),
    birthDate: keepText(applicant.birthDate, customer.birthDate),
    genderId: keepEnum<typeof applicant.genderId, EGender>(applicant.genderId, customer.genderId, getEGenderList()),
    languageId: keepEnum<typeof applicant.languageId, ELanguage>(
      applicant.languageId,
      loan.languageId,
      getELanguageList(),
    ),
    maritalStatusId: EMaritalStatus.Undefined,
    email: keepText(applicant.email, customer.email),
    cellPhone: keepDigits(applicant.cellPhone, customer.cellPhone),
    homePhone: keepDigits(applicant.homePhone, customer.homePhone),
    currentAddress: {
      ...applicant.currentAddress,
      civicNumber: keepText(applicant.currentAddress.civicNumber, address.civicNumber),
      suite: keepText(applicant.currentAddress.suite, address.suite),
      apartment: keepText(applicant.currentAddress.apartment, address.apartment),
      street: keepText(applicant.currentAddress.street, address.street),
      city: keepText(applicant.currentAddress.city, address.city),
      postalCode: keepText(applicant.currentAddress.postalCode, address.postalCode),
      country: keepText(applicant.currentAddress.country, address.country),
      stateIso: keepProvince(applicant.currentAddress.stateIso, address.stateIso),
    },
  }
}
